// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import Select2, { DataSelect2 } from 'wdna-select2';

import { UserDO } from 'wdna-components';
import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table from 'wdna-components/lib/table/Table';
import { messageError, messageWarning } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { client, company, CompanyDO } from '../../../management/requests';

type ReportDO = {
  id: number;
  name: string;
}

type Props = unknown;

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;
  companyId?: number;
  companiesArray: CompanyDO[];

  data: ReportDO[];
}

export default class System extends React.Component<Props, State> {
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;

    this.state = {
      loading: false,
      update: window.getStr(),
      showGrid: false,
      companiesArray: [],
      data: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    const user: UserDO = _.get(window.store.getState(), ['user', 'user']);
    this.loadingStart();
    try {
      const companiesArray: CompanyDO[] = await company<CompanyDO[]>('list') || [];
      this.setState({ companiesArray });
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onGo = (): void => {
    const { companyId } = this.state;
    if (companyId !== undefined) {
      const req: Record<string, any> | null = { companyId };

      console.log(req);

      this.loadingStart();
      client<string[]>('reports', undefined, req)
        ?.then((resp: string[]) => {
          const data: ReportDO[] = resp.map((item: string, index: number): ReportDO => { return { id: index, name: item, }; });
          this.setState({
            data
          });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    } else {
      messageWarning('Warning', 'You must select a company to get the report list');
    }
  }

  public onInfo = (value: ReportDO): void => {
    console.log(value);

    const { companyId } = this.state;
    const req: Record<string, any> | null = { companyId };

    this.loadingStart();
    client('report', value.name, req)
      ?.then(() => {
        /* pass */
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public handleChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  }

  public render(): JSX.Element {
    const { loading, data, showGrid, companiesArray, companyId } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'Id',
      name: 'File Name',
    };
    const columnsToHide: string[] = [];
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      name: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const customColumnsDefs: Record<string, any> = {
      id: { suppressSizeToFit: false, minWidth: 50, maxWidth: 50 },
    };

    return (
      <div id="content" className="content">
        <div className="row">
          <div className="col-3 wdna-padding">
            &nbsp;&nbsp;&nbsp;
            <Select2
              name="companyId"
              data={companiesArray?.map((item: CompanyDO): DataSelect2 => {
                return { text: item.name, id: item.id };
              })}
              value={companyId}
              options={{
                placeholder: 'Select company',
              }}
              onChange={this.handleChange}
              style={{ width: '150px' }}
            />
          </div>
          <div className="col-6 wdna-padding">
            <button type="button" className="filter-btn btn btn-default" onClick={this.onGo}>
              <i className="wdna-ico-icon-playsearch" />
              <span>GO</span>
            </button>
          </div>
        </div>
        <div style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
          <Table
            data={data || []}
            alias={columnAlias}
            hiddenCols={columnsToHide}
            customCellStyles={columnStyles}
            customColumnsDefs={customColumnsDefs}
            buttons={{ info: true, export: false, filters: false, reset: false }}
            onInfo={this.onInfo}
            loading={loading}
            rowDataChanged={this.rowDataChanged}
            pagination={false}
            showRowNumber
          />
        </div>
      </div>
    );
  }
}
